import React from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import {
  AppFlowModal,
  BackNavRow,
  CapsuleButton as BaseCapsuleButton,
  LoadingIndicator,
  PageContainer,
  PrimaryButton,
} from "../common/CommonStyles";
import { NavContext } from "../common/context/NavContext";
import { useTranslation } from "react-i18next";
import { useLiveData } from "../common/utils";
import { Account, AccountContext } from "../common/context/AccountContext";
import {
  isSchoolUser,
  SpeakingRate,
  TeacherAvatarInfo,
  Profile,
} from "../common/api";
import { ApiContext } from "../common/context/ApiContext";
import { Alert } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleExclamation,
  faPlay,
  faSliders,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import {
  PronunciationInput,
  playAudio,
} from "../components/pronunciation/PronunciationInput";
import {
  AnalyticsContext,
  ViewEditProfileEvent,
} from "../common/context/AnalyticsContext";
import {
  COLOR_NEUTRAL_BORDER,
  COLOR_RED,
  COLOR_SHADOW_LIGHT,
  COLOR_SUCCESS,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_TERTIARY,
  COLOR_WHITE,
} from "../common/colors";
import { SPACE_LG, SPACE_MD, SPACE_SM, SPACE_XL } from "../common/spacing";
import { SURFACE_CARD_RADIUS_SMALL } from "../common/constants";

const DEFAULT_TEACHER_AVATAR_ID = 1;
const FIELD_MAX_WIDTH = 480;
const GRID_MAX_WIDTH = 800;
const BUTTON_HEIGHT = 56;

const Column = styled.div<{ $maxWidth: number }>`
  max-width: ${(p) => p.$maxWidth}px;
  margin: 0 auto;
`;

const FieldLabel = styled.label`
  display: block;
  padding: ${SPACE_SM}px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${COLOR_TEXT_PRIMARY};
`;

const NameInput = styled.input`
  width: 100%;
  padding: ${SPACE_LG}px ${SPACE_MD}px;
  border: 1px solid ${COLOR_NEUTRAL_BORDER};
  border-radius: ${SURFACE_CARD_RADIUS_SMALL}px;
  font-size: 16px;
  color: ${COLOR_TEXT_PRIMARY};

  ::placeholder {
    color: ${COLOR_TEXT_TERTIARY};
  }
`;

const CapsuleButton = styled(BaseCapsuleButton)`
  margin-top: ${SPACE_SM}px;
`;

const DeleteButton = styled(CapsuleButton)`
  height: ${BUTTON_HEIGHT}px;
  border: 1px solid ${COLOR_NEUTRAL_BORDER};
  background-color: ${COLOR_WHITE};
  color: ${COLOR_RED};
`;

const Rule = styled.hr`
  margin: ${SPACE_XL}px ${SPACE_SM}px;
  border: none;
  border-top: 1px solid ${COLOR_NEUTRAL_BORDER};
  opacity: 1;
`;

const TeacherGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);

  @media (min-width: 600px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const TeacherCell = styled.button`
  position: relative;
  padding: ${SPACE_SM}px;
  border: none;
  background: none;

  :disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const TeacherImage = styled.img<{ $isSelected: boolean }>`
  display: block;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  box-shadow: ${(p) =>
    p.$isSelected
      ? "0 6px 28px rgba(0, 0, 0, 0.2)"
      : `0 1px 4px ${COLOR_SHADOW_LIGHT}`};
  object-fit: cover;
  transition: box-shadow 0.2s;
`;

const TeacherCheck = styled(FontAwesomeIcon)`
  position: absolute;
  top: ${SPACE_SM}px;
  right: ${SPACE_SM}px;
  font-size: 24px;
  border-radius: 50%;
  background-color: ${COLOR_WHITE};
  color: ${COLOR_SUCCESS};
`;

const SaveBar = styled.div`
  position: sticky;
  bottom: 0;
  padding: ${SPACE_XL}px 0 ${SPACE_LG}px;
  background: linear-gradient(transparent, ${COLOR_WHITE} ${SPACE_XL}px);
`;

const SaveButton = styled(PrimaryButton)`
  display: block;
  width: 100%;
  height: ${BUTTON_HEIGHT}px;
  border-radius: 100px;
  font-size: 16px;
`;

const enum SaveProfileResult {
  NONE,
  EDIT_ERROR,
  CREATE_ERROR,
}

const enum Mode {
  EDIT_PROFILE,
  EDIT_PRONUNCIATION,
}

export const EditProfilePage: React.FC<{ isForCreate: boolean }> = ({
  isForCreate,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = React.useState(Mode.EDIT_PROFILE);
  const { accountLiveData, updateProfile, addProfile, removeProfile } =
    React.useContext(AccountContext);
  const {
    checkUserInput,
    modifyProfile,
    createProfile,
    deleteProfile,
    getTeacherAvatars,
    previewPhonics,
  } = React.useContext(ApiContext);
  const { goBack } = React.useContext(NavContext);
  const [userIsSchoolUser, setUserIsSchoolUser] = React.useState<boolean>();
  const [saveProfileResult, setSaveProfileResult] = React.useState(
    SaveProfileResult.NONE
  );
  const [saveProfileErrorMessage, setSaveProfileErrorMessage] =
    React.useState("");
  const [rosterName, setRosterName] = React.useState("");
  const [tempFirstName, setTempFirstName] = React.useState("");
  const [tempFirstNamePhonetics, setTempFirstNamePhonetics] = React.useState<
    string[]
  >([]);
  const [tempTeacherAvatarId, setTempTeacherAvatarId] = React.useState(
    DEFAULT_TEACHER_AVATAR_ID
  );
  const [teacherAvatars, setTeacherAvatars] =
    React.useState<TeacherAvatarInfo[]>();
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState(false);
  const { logEvent } = React.useContext(AnalyticsContext);
  const [hasInvalidProfileId, setHasInvalidProfileId] =
    React.useState<boolean>();
  const [canCreateProfile, setCanCreateProfile] = React.useState<boolean>();
  const { profileId }: { profileId?: string } = useParams<{
    profileId?: string;
  }>();
  const [profileToEdit, setProfileToEdit] = React.useState<Profile>();

  React.useEffect(() => {
    if (mode === Mode.EDIT_PROFILE) {
      logEvent(new ViewEditProfileEvent());
    }
  }, [logEvent, mode]);

  React.useEffect(() => {
    getTeacherAvatars().then(({ teacher_avatars_info }) =>
      setTeacherAvatars(teacher_avatars_info)
    );
  }, [getTeacherAvatars]);

  const trimmedFirstName = tempFirstName.trim();
  const playPronunciation = () => {
    setIsPreviewLoading(true);
    previewPhonics(tempFirstNamePhonetics.join(""), trimmedFirstName)
      .then(playAudio)
      .finally(() => setIsPreviewLoading(false));
  };

  const makeProfile = React.useCallback(
    async (firstName, firstNamePhonetics, teacherAvatar) => {
      setSaveProfileResult(SaveProfileResult.NONE);

      const result = await checkUserInput(firstName);
      if (!result || result.result === "error") {
        setSaveProfileResult(SaveProfileResult.CREATE_ERROR);
        setSaveProfileErrorMessage(result?.error_message || "");
        return;
      }

      const newProfile = await createProfile(
        firstName,
        firstNamePhonetics,
        SpeakingRate.NORMAL,
        0,
        teacherAvatar,
        true
      );

      if (newProfile !== undefined) {
        addProfile(newProfile);
        goBack();
      } else {
        setSaveProfileResult(SaveProfileResult.CREATE_ERROR);
        setSaveProfileErrorMessage("");
      }
    },
    [
      goBack,
      addProfile,
      createProfile,
      checkUserInput,
      setSaveProfileResult,
      setSaveProfileErrorMessage,
    ]
  );

  const deleteUserProfile = React.useCallback(
    async (profile) => {
      setSaveProfileResult(SaveProfileResult.NONE);

      await deleteProfile(profile.id);
      removeProfile(profile);
      setSaveProfileErrorMessage("");
      goBack();
    },
    [
      goBack,
      removeProfile,
      deleteProfile,
      setSaveProfileResult,
      setSaveProfileErrorMessage,
    ]
  );

  const updateProfileFields = React.useCallback(
    async (firstName, firstNamePhonetics, teacherAvatar) => {
      if (!profileToEdit) {
        return;
      }

      setSaveProfileResult(SaveProfileResult.NONE);

      const result = await checkUserInput(firstName);
      if (!result || result.result === "error") {
        setSaveProfileResult(SaveProfileResult.EDIT_ERROR);
        setSaveProfileErrorMessage(result?.error_message || "");
        return;
      }

      const profile = profileToEdit!!;
      const updatedProfile = await modifyProfile(
        profile.id,
        firstName,
        firstNamePhonetics,
        profile.speaking_rate,
        profile.asr_english_accent,
        teacherAvatar,
        profile.closed_captions_enabled,
        true
      );

      if (updatedProfile !== undefined) {
        updateProfile(updatedProfile);
        goBack();
      } else {
        setSaveProfileResult(SaveProfileResult.EDIT_ERROR);
        setSaveProfileErrorMessage("");
      }
    },
    [
      goBack,
      profileToEdit,
      updateProfile,
      modifyProfile,
      checkUserInput,
      setSaveProfileResult,
      setSaveProfileErrorMessage,
    ]
  );

  useLiveData(
    React.useCallback(
      (account: Account) => {
        if (account.hasUserAndMaybeProfile() && isForCreate !== undefined) {
          if (isForCreate && !account.canCreateProfile()) {
            setCanCreateProfile(false);
            return;
          }
          var profile;
          if (!isForCreate) {
            if (profileId === undefined) {
              setHasInvalidProfileId(true);
            } else {
              profile = account.user.profiles.find((profile) => {
                return profile.id === +profileId;
              });
              setProfileToEdit(profile);
              setHasInvalidProfileId(profile === undefined);
            }
          }
          if (!isForCreate && profile === undefined) {
            return;
          }

          setUserIsSchoolUser(isSchoolUser(account.user));
          setRosterName(
            isSchoolUser(account.user)
              ? (profile?.roster_first_name || "") +
                  " " +
                  (profile?.roster_last_name || "")
              : ""
          );
          setTempFirstName(isForCreate ? "" : profile?.first_name || "");
          setTempFirstNamePhonetics(
            isForCreate ? [] : profile?.first_name_phonetics || []
          );
          setTempTeacherAvatarId(
            profile?.teacher_avatar_info.id ?? DEFAULT_TEACHER_AVATAR_ID
          );
        }
      },
      [isForCreate, profileId, setProfileToEdit]
    ),
    accountLiveData
  );

  if (canCreateProfile === false || hasInvalidProfileId === true) {
    return <></>;
  }
  if (mode === Mode.EDIT_PRONUNCIATION) {
    return (
      <PronunciationInput
        initialPhonetics={tempFirstNamePhonetics}
        onPronunciationUpdated={(newPhonetics) => {
          setTempFirstNamePhonetics(newPhonetics);
          setMode(Mode.EDIT_PROFILE);
        }}
      ></PronunciationInput>
    );
  }

  return (
    <PageContainer>
      {saveProfileResult === SaveProfileResult.EDIT_ERROR && (
        <Alert
          icon={
            <FontAwesomeIcon icon={faCircleExclamation} fontSize="inherit" />
          }
          severity="error"
          style={{ marginBottom: "15px" }}
        >
          {t("edit_profile_update_error") +
            (saveProfileErrorMessage ? ": " + saveProfileErrorMessage : "")}
        </Alert>
      )}
      {saveProfileResult === SaveProfileResult.CREATE_ERROR && (
        <Alert
          icon={
            <FontAwesomeIcon icon={faCircleExclamation} fontSize="inherit" />
          }
          severity="error"
          style={{ marginBottom: "15px" }}
        >
          {t("edit_profile_create_error") +
            (saveProfileErrorMessage ? ": " + saveProfileErrorMessage : "")}
        </Alert>
      )}
      <BackNavRow label={t("generic_nav_back")}>
        <h2>
          {t(
            isForCreate
              ? "edit_profile_add_profile"
              : "edit_profile_edit_profile"
          )}
        </h2>
      </BackNavRow>

      <Column $maxWidth={FIELD_MAX_WIDTH} className="mt-4">
        {/* School users can see their "roster name"
            and can only edit their "nickname" aka profile.first_name */}
        {userIsSchoolUser && (
          <div className="mb-2">
            <b>{t("edit_profile_full_name")}</b>
            <span className="ms-2" style={{ color: COLOR_TEXT_SECONDARY }}>
              {rosterName}
            </span>
          </div>
        )}
        <FieldLabel htmlFor="firstName">
          {userIsSchoolUser
            ? t("edit_profile_nickname")
            : t("edit_profile_first_name")}
        </FieldLabel>
        <NameInput
          id="firstName"
          autoComplete="given-name"
          maxLength={255}
          placeholder={t("edit_profile_first_name_placeholder")}
          value={tempFirstName}
          onChange={(e) => setTempFirstName(e.target.value)}
        />
        {trimmedFirstName && (
          <>
            <CapsuleButton
              onClick={playPronunciation}
              disabled={isPreviewLoading}
            >
              {isPreviewLoading ? (
                <LoadingIndicator />
              ) : (
                <FontAwesomeIcon icon={faPlay} />
              )}
              {t("edit_profile_pronounce_name", { name: trimmedFirstName })}
            </CapsuleButton>
            <CapsuleButton onClick={() => setMode(Mode.EDIT_PRONUNCIATION)}>
              <FontAwesomeIcon icon={faSliders} />
              {t("edit_profile_customize_pronunciation")}
            </CapsuleButton>
          </>
        )}
      </Column>
      <Column $maxWidth={GRID_MAX_WIDTH}>
        <Rule />
        <FieldLabel className="mx-2">
          {t("edit_profile_teacher_appearance")}
        </FieldLabel>
        {teacherAvatars === undefined ? (
          <div className="text-center py-4">
            <LoadingIndicator size="2x" />
          </div>
        ) : (
          <TeacherGrid>
            {teacherAvatars.map((avatar) => (
              <TeacherCell
                key={avatar.id}
                disabled={!avatar.is_available}
                onClick={() => setTempTeacherAvatarId(avatar.id)}
              >
                <TeacherImage
                  src={avatar.image_url}
                  alt={avatar.name}
                  $isSelected={avatar.id === tempTeacherAvatarId}
                />
                {avatar.id === tempTeacherAvatarId && (
                  <TeacherCheck icon={faCircleCheck} />
                )}
              </TeacherCell>
            ))}
          </TeacherGrid>
        )}
        {!isForCreate &&
          profileToEdit?.is_creator === false &&
          !userIsSchoolUser && (
            <>
              <Rule />
              <Column $maxWidth={FIELD_MAX_WIDTH}>
                <DeleteButton onClick={() => setIsConfirmingDelete(true)}>
                  <FontAwesomeIcon icon={faTrashCan} />
                  {t("edit_profile_delete_profile")}
                </DeleteButton>
              </Column>
            </>
          )}
      </Column>
      <SaveBar>
        <Column $maxWidth={FIELD_MAX_WIDTH}>
          <SaveButton
            disabled={!trimmedFirstName || (!isForCreate && !profileToEdit)}
            onClick={() =>
              (isForCreate ? makeProfile : updateProfileFields)(
                trimmedFirstName,
                tempFirstNamePhonetics,
                tempTeacherAvatarId
              )
            }
          >
            {t("generic_save")}
          </SaveButton>
        </Column>
      </SaveBar>
      <AppFlowModal
        title={t("edit_profile_delete_title")}
        body={t("edit_profile_delete_message")}
        closeButtonText={t("generic_cancel")}
        shouldShowModal={isConfirmingDelete}
        closeModal={() => setIsConfirmingDelete(false)}
        submitButtonText={t("generic_ok")}
        onClickSubmit={() => {
          setIsConfirmingDelete(false);
          deleteUserProfile(profileToEdit);
        }}
      />
    </PageContainer>
  );
};
